// PATH: src/api/apiClients.ts
// Aligné sur tc_tilmicode_ui : session portée par le cookie httpOnly tc_session
// (SSO tc_identity) → withCredentials, aucun Bearer injecté. Un 401 sur une
// route protégée redirige vers /login.
//
// IMPORTANT stabilité : l'instance axios est mémoïsée sur [baseURL, timezone]
// UNIQUEMENT. Le handler d'erreur est lu via une ref — une closure fraîche par
// render ne doit JAMAIS invalider le client, sinon chaque useEffect dépendant
// de l'api se relance en boucle (appels infinis).
import { useMemo, useRef } from "react";
import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  type AxiosInstance,
  type AxiosRequestHeaders,
} from "axios";
import { useNavigate } from "react-router-dom";
import { buildBaseUrl, apiTimeout, type ApiService } from "../config/config";
import { emitUnauthorized } from "./authEvents";

// Routes publiques : pas de redirection automatique sur 401.
const PUBLIC_PATHS = new Set<string>(["/login"]);

const useHandleError = () => {
  const navigate = useNavigate();

  return (error: AxiosError | unknown): void => {
    const status = (error as AxiosError)?.response?.status;

    if (status === 401) {
      // Prévenir le contexte AVANT de rediriger : il doit repasser à
      // « anonymous », sinon /login se croirait encore connecté et renverrait
      // vers l'application — boucle de redirection.
      emitUnauthorized();
      try {
        const pathname = window.location?.pathname ?? "/";
        if (!PUBLIC_PATHS.has(pathname)) {
          navigate("/login", { replace: true });
        }
      } catch {
        navigate("/login", { replace: true });
      }
      return;
    }

    if (!(error as AxiosError)?.response) {
      // eslint-disable-next-line no-console
      console.error("Erreur réseau ou CORS:", error);
    }
  };
};

export const useApiClient = (
  service: ApiService = "tilmischool"
): AxiosInstance => {
  const handleError = useHandleError();
  // Ref toujours à jour, sans jamais invalider le useMemo ci-dessous.
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const baseURL = buildBaseUrl(service);

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL,
      timeout: apiTimeout,
      // IMPORTANT: on s'appuie sur les cookies httpOnly -> il faut withCredentials
      withCredentials: true,
    });

    instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const headers = (config.headers ?? {}) as AxiosRequestHeaders;
        headers["X-Timezone"] = timezone;
        config.headers = headers;
        return config;
      },
      (error: unknown) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        handleErrorRef.current(error);
        return Promise.reject(error);
      }
    );

    return instance;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseURL, timezone]);

  return apiClient;
};
